/**
 * Suggestion Copy Utility
 * Provides context-appropriate, benefit-driven messaging for chat suggestions
 * Following clear, conversational tone focused on user needs
 */

import type { SuggestionCategory } from '../types/chat.types';

/**
 * Get the off-topic header message
 * @returns Clear, conversational message explaining the AI's purpose
 */
export function getOffTopicHeader(): string {
  return "I'm Hajra, and I'm here to help with questions about AmalQ, donations, Islamic charity, and platform features.";
}

/**
 * Get the suggestions label
 * @returns Label for the suggestions section
 */
export function getSuggestionsLabel(): string {
  return "I can help you with:";
}

/**
 * Get category label for display
 * @param category - The suggestion category
 * @returns Natural, benefit-focused category description
 */
export function getCategoryLabel(category: SuggestionCategory): string {
  switch (category) {
    case 'platform':
      return 'Platform Features';
    case 'donation':
      return 'Donations & Giving';
    case 'islamic':
      return 'Islamic Charity';
    case 'campaign':
      return 'Campaigns';
    default:
      return 'Helpful Topics';
  }
}

/**
 * Get category description
 * @param category - The suggestion category
 * @returns Benefit-driven description of what the category offers
 */
export function getCategoryDescription(category: SuggestionCategory): string {
  switch (category) {
    case 'platform':
      return 'Learn how to use AmalQ features and tools';
    case 'donation':
      return 'Make donations and understand giving options';
    case 'islamic':
      return 'Learn about Zakat, Sadaqah, and Islamic charity';
    case 'campaign':
      return 'Create and manage charitable campaigns';
    default:
      return 'Get help with this topic';
  }
}

/**
 * Get tooltip text for off-topic indicator
 * @returns Tooltip explaining why suggestions are shown
 */
export function getOffTopicTooltip(): string {
  return "This question is outside my expertise. Here are topics I can help with.";
}

/**
 * Get accessibility label for suggestion button
 * @param suggestionText - The suggestion text
 * @param category - The suggestion category
 * @returns ARIA label for screen readers
 */
export function getSuggestionAriaLabel(suggestionText: string, category: SuggestionCategory): string {
  const categoryLabel = getCategoryLabel(category);
  return `Select suggestion: ${suggestionText} (${categoryLabel})`;
}

/**
 * Get suggestion click action description
 * @param hasActionUrl - Whether the suggestion has an action URL
 * @returns Description of what happens when suggestion is clicked
 */
export function getSuggestionActionDescription(hasActionUrl: boolean): string {
  if (hasActionUrl) {
    return 'Click to ask about this topic and navigate to the relevant page';
  }
  return 'Click to ask about this topic';
}

